import * as Sentry from '@sentry/nextjs'

let visitorIdPromise: Promise<string | null> | null = null

const FINGERPRINT_API_KEY = process.env.NEXT_PUBLIC_FINGERPRINT_API_KEY

/**
 * Fingerprint error codes that come from the visitor's side, not ours. The
 * v4 agent has no network_restricted code; v3's firewall case now surfaces as
 * one of the network codes below.
 */
const CLIENT_SIDE_ERROR_CODES: readonly string[] = [
  'script_load_fail',
  'csp_block',
  'client_timeout',
  'network_connection',
  'network_abort',
]

/**
 * Start the Fingerprint agent and resolve its visitor id.
 * Cached so the agent is only started once per page session.
 * Never throws — returns null on any failure.
 */
async function loadVisitorId(): Promise<string | null> {
  if (typeof window === 'undefined') return null

  if (!FINGERPRINT_API_KEY) {
    console.warn('[Fingerprint] NEXT_PUBLIC_FINGERPRINT_API_KEY not configured')
    return null
  }

  let Fingerprint: typeof import('@fingerprint/agent')
  try {
    Fingerprint = await import('@fingerprint/agent')
  } catch (err) {
    // The SDK chunk itself failed to load, so there is no error code to read.
    reportAgentFailure(err, null)
    return null
  }

  try {
    const agent = Fingerprint.start({
      apiKey: FINGERPRINT_API_KEY,
      // Custom subdomain: the agent script comes from <endpoint>/web/v4/<key>,
      // with Fingerprint's own endpoints as the fallback.
      endpoints: 'https://bouncer.martini.film',
    })
    const result = await agent.get()
    return result.visitor_id ?? null
  } catch (err) {
    reportAgentFailure(err, Fingerprint.isFingerprintError(err) ? err.code : null)
    return null
  }
}

/**
 * The agent fails on the visitor's side far more often than ours: ad blockers
 * stop the script loading, firewalls restrict it, and flaky networks drop or
 * time out its request. Those (and anything while the browser is offline)
 * produced ~18k Sentry events that nobody can act on, so they stay a breadcrumb. Anything else (a bad key, forbidden origin, rate limit, a proxy
 * failure at bouncer.martini.film) is our configuration and still reports.
 */
function reportAgentFailure(err: unknown, code: string | null): void {
  const reason = err instanceof Error ? err.message : String(err)
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false
  if (offline || (code !== null && CLIENT_SIDE_ERROR_CODES.includes(code))) {
    Sentry.addBreadcrumb({
      category: 'fingerprint',
      message: `Fingerprint agent unavailable: ${code ?? reason}`,
      level: 'info',
    })
    return
  }
  Sentry.captureMessage('Fingerprint agent failed', {
    level: 'warning',
    tags: { source: 'fingerprint', ...(code ? { fingerprint_code: code } : {}) },
    extra: { reason },
  })
}

/**
 * Get the device visitorId. Safe to call multiple times — the agent is
 * loaded once and the result is cached. Returns null if the agent fails
 * to load or produce a result (ad-blocker, network error, etc.).
 */
export async function getVisitorId(): Promise<string | null> {
  if (!visitorIdPromise) {
    visitorIdPromise = loadVisitorId()
  }
  return visitorIdPromise
}
